"use client";

import { type FC, type Ref, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  ActionCard,
  Flex,
  Icon,
  Indicator,
  Spacing,
  type UIAction,
  useActions,
  useThemeSlot,
} from "@huub-web/ui";
import type { IconName } from "@huub-web/ui/theme";
import type { ThemeScope } from "@huub-web/ui/contexts/ThemeSlotContext";

import { useTemplates } from "../../hooks/useTemplates";
import { usePageContent } from "../../hooks/usePageContent";

type DefaultVisualProps = {
  scope: ThemeScope;
  size?: number;
};

const ICON_MAP: Partial<Record<ThemeScope, IconName>> = {
  maximumMortgage: "calculator",
  orientation: "houseSearch",
  financialProfile: "certificate",
  interestRates: "percentSquare",
};

const DefaultVisual: FC<DefaultVisualProps> = ({ scope }) => {
  return <Indicator icon={ICON_MAP[scope]} type="square" size="l" />;
};

export type ActionHubCardTemplateProps = {
  /**
   * The scope that determines which action card this represents
   */
  scope: ThemeScope;
  /**
   * Optional override for the card title; falls back to i18n key
   */
  title?: string;
  /**
   * Optional override for the card description; falls back to i18n key
   */
  description?: string;
  /**
   * Whether the action associated with this card has been completed
   */
  completed?: boolean;
  /**
   * The action to perform when the card is clicked
   */
  action?: UIAction;
  /**
   * Whether the card is disabled
   */
  disabled?: boolean;
  /**
   * Ref forwarded to the root element
   */
  ref?: Ref<HTMLDivElement>;
};

export const ActionHubCardTemplate: FC<ActionHubCardTemplateProps> = ({
  scope,
  completed,
  action,
  disabled,
  title,
  description,
  ref,
}) => {
  const { t } = useTranslation();
  const { lockPrompts, lockUI } = useTemplates();
  const { handleAction } = useActions();

  const Visual = useThemeSlot({
    key: "scopeVisual",
    defaultComponent: DefaultVisual,
  });

  const resolvedTitle = title ?? t(`actionHubCard.${scope}.title`);
  const resolvedDescription =
    description ?? t(`actionHubCard.${scope}.description`);

  const contentItem = useMemo(
    () => ({
      type: "card" as const,
      label: resolvedTitle,
      description: resolvedDescription,
      meta: {
        scope,
        ...(completed ? { completed } : {}),
      },
      ...(action ? { action } : {}),
    }),
    [resolvedTitle, resolvedDescription, action, scope, completed],
  );
  usePageContent(`actionhubcard-${scope}`, contentItem);

  const isDisabled = (() => {
    if (action?.type === "prompt") {
      return lockPrompts;
    }
    return lockUI || disabled;
  })();

  const handleClick = () => {
    if (action) {
      handleAction(action);
    }
  };

  return (
    <ActionCard
      ref={ref}
      border="default"
      disabled={isDisabled}
      onClick={handleClick}
      title={resolvedTitle}
      body={resolvedDescription}
      variant="secondary"
      padding="l"
      leading={
        <>
          <Flex justify="space-between">
            <Visual scope={scope} size={72} />
            {completed && <Icon icon="checkCircleFilled" color="gradient" />}
          </Flex>
          <Spacing height="xxs" />
        </>
      }
    />
  );
};
